/**
 * a2ui 1.0 engine — the framework-agnostic core our session renderer, widget
 * wrappers and conformance smoke all share.
 *
 * WHY THIS EXISTS (and not `@a2ui/web_core`): the upstream package pins `lit`
 * + `@lit/context` (a web-components renderer) and `zod@^3` (we ship `zod@^4`).
 * We reuse our OWN React kit via smart wrappers, so a Lit renderer is the wrong
 * shape and the version conflict is real. The pieces a wrapper actually needs —
 * an RFC-6901 reactive data model, the `${…}` expression parser/evaluator, and
 * the catalog function set — are small and fully pinned by the vendored
 * conformance corpus (`contracts/a2ui/conformance/core/*`), which this module is
 * verified against. See `a2ui/__tests__` + the widget-native conformance smoke.
 *
 * Nothing here touches React or the DOM: it is data-model + expression only.
 */

// ─── RFC-6901 JSON Pointer ─────────────────────────────────────────────────

/** Unescape one JSON-Pointer reference token (`~1`→`/`, `~0`→`~`). */
export function unescapeToken(tok) {
  return String(tok).replace(/~1/g, '/').replace(/~0/g, '~');
}

/** Escape one path segment into a JSON-Pointer reference token. */
export function escapeToken(seg) {
  return String(seg).replace(/~/g, '~0').replace(/\//g, '~1');
}

/**
 * Split a JSON Pointer into its (unescaped) segments. `""` / `"/"` → `[]`
 * (root). A pointer must start with `/`; a relative dotted path is accepted too
 * (our binds arrive dotted before conversion) and split on `.`/`[n]`.
 */
export function pointerSegments(pointer) {
  let p = String(pointer == null ? '' : pointer);
  if (p === '' || p === '/') return [];
  // a trailing slash is not a distinct path ("/foo/" === "/foo")
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  if (p.startsWith('/')) {
    return p.slice(1).split('/').map(unescapeToken);
  }
  // dotted/bracketed relative path (pre-conversion binds, expression relatives)
  return p
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((s) => s !== '');
}

class DataError extends Error {
  constructor(message) { super(message); this.name = 'DataError'; }
}

/** Read the value at `pointer` from `root`. Missing → `undefined`. */
export function getPointer(root, pointer) {
  const segs = pointerSegments(pointer);
  let cur = root;
  for (const seg of segs) {
    if (cur == null) return undefined;
    if (Array.isArray(cur)) {
      if (!/^\d+$/.test(seg)) return undefined;
      cur = cur[Number(seg)];
    } else if (typeof cur === 'object') {
      cur = cur[seg];
    } else {
      return undefined;
    }
  }
  return cur;
}

/**
 * Set `value` at `pointer` in `root` (mutating). RFC-6901 + a2ui 1.0 rules:
 *   - intermediate objects auto-vivify (numeric next-segment ⇒ array)
 *   - `pointer === '/'` (or '') REPLACES the whole model; returns the new root
 *   - `value === null` on an OBJECT key deletes the key; on an ARRAY index sets
 *     that index to null in place (length preserved) — the deliberate asymmetry
 *   - a leading-zero array index (`/items/01`) is a DataError
 *   - writing through a primitive (`/user/name/first` when /user is a string) is
 *     a DataError
 */
export function setPointer(root, pointer, value) {
  const segs = pointerSegments(pointer);
  if (segs.length === 0) {
    // whole-model replace; `null` ⇒ reset to {}
    return value == null ? {} : value;
  }
  let cur = root == null ? {} : root;
  const out = cur;
  for (let i = 0; i < segs.length - 1; i += 1) {
    const seg = segs[i];
    const nextSeg = segs[i + 1];
    if (Array.isArray(cur)) {
      if (!/^\d+$/.test(seg)) throw new DataError(`non-numeric segment "${seg}" for array`);
      if (seg.length > 1 && seg[0] === '0') throw new DataError(`non-numeric segment "${seg}" (leading zero)`);
      const idx = Number(seg);
      if (cur[idx] == null || typeof cur[idx] !== 'object') {
        cur[idx] = /^\d+$/.test(nextSeg) ? [] : {};
      }
      cur = cur[idx];
    } else if (cur && typeof cur === 'object') {
      if (cur[seg] == null || typeof cur[seg] !== 'object') {
        cur[seg] = /^\d+$/.test(nextSeg) ? [] : {};
      }
      cur = cur[seg];
    } else {
      throw new DataError(`Cannot set path through non-object at "${seg}"`);
    }
  }
  const last = segs[segs.length - 1];
  if (Array.isArray(cur)) {
    if (!/^\d+$/.test(last)) throw new DataError(`non-numeric segment "${last}" for array`);
    if (last.length > 1 && last[0] === '0') throw new DataError(`non-numeric segment "${last}" (leading zero)`);
    cur[Number(last)] = value; // array index set-in-place, null included
  } else if (cur && typeof cur === 'object') {
    if (value === null) delete cur[last];
    else cur[last] = value;
  } else {
    throw new DataError(`Cannot set path "${last}" on non-object`);
  }
  return out;
}

/** Delete the value at `pointer` (mutating). Root ⇒ `{}`. */
export function deletePointer(root, pointer) {
  const segs = pointerSegments(pointer);
  if (segs.length === 0) return {};
  const parent = getPointer(root, '/' + segs.slice(0, -1).map(escapeToken).join('/'));
  const last = segs[segs.length - 1];
  if (parent == null || typeof parent !== 'object') return root;
  if (Array.isArray(parent)) {
    // deleting an out-of-range index is a no-op (never pad the list out)
    if (/^\d+$/.test(last) && Number(last) < parent.length) parent[Number(last)] = null; // in place
  } else {
    delete parent[last];
  }
  return root;
}

// ─── reactive data model (observers, for the conformance data_model cases) ──

export function createDataModel(initial = {}) {
  let root = initial == null ? {} : structuredClone(initial);
  const observers = new Map(); // path → Set<fn>
  let disposed = false;

  const notify = (path) => {
    // notify the exact path and any ancestor observer whose subtree changed
    for (const [p, set] of observers.entries()) {
      if (p === path || path.startsWith(p === '/' ? '/' : p + '/') || p === '/') {
        for (const fn of set) fn(getPointer(root, p));
      }
    }
  };

  return {
    get: (pointer = '/') => getPointer(root, pointer),
    set: (pointer, value) => {
      if (disposed) return;
      root = setPointer(root, pointer, value);
      notify(pointer);
    },
    delete: (pointer) => {
      if (disposed) return;
      root = deletePointer(root, pointer);
      notify(pointer);
    },
    replace: (value) => { root = value == null ? {} : value; notify('/'); },
    observe: (pointer, fn) => {
      if (!observers.has(pointer)) observers.set(pointer, new Set());
      observers.get(pointer).add(fn);
      return () => observers.get(pointer)?.delete(fn);
    },
    dispose: () => { disposed = true; observers.clear(); },
    snapshot: () => structuredClone(root),
    get root() { return root; },
  };
}

// ─── `${…}` expression parser ──────────────────────────────────────────────

class ParseError extends Error {
  constructor(message) { super(message); this.name = 'ParseError'; }
}

/**
 * Parse a `formatString` template into ordered parts. Each part is one of:
 *   { literal: string }
 *   { path: string }                              (data binding)
 *   { call: string, args: {name→part}, returnType }
 * Nested interpolation and `\${` escapes are handled. Empty template → [].
 */
export function parseTemplate(input) {
  const src = String(input == null ? '' : input);
  const parts = [];
  let lit = '';
  let i = 0;
  const flushLit = () => { if (lit) { parts.push({ literal: lit }); lit = ''; } };

  while (i < src.length) {
    if (src[i] === '\\' && src[i + 1] === '$') { lit += '$'; i += 2; continue; }
    if (src[i] === '$' && src[i + 1] === '{') {
      flushLit();
      const [expr, next] = readInterpolation(src, i + 2);
      parts.push(parseExpr(expr));
      i = next;
      continue;
    }
    lit += src[i];
    i += 1;
  }
  flushLit();
  return parts;
}

/** Read up to the matching `}` from position `start`; returns [inner, nextIdx]. */
function readInterpolation(src, start) {
  let depth = 1;
  let i = start;
  let out = '';
  while (i < src.length) {
    const c = src[i];
    if (c === '\\' && src[i + 1] === '$') { out += '${'; i += 2; continue; }
    if (c === '$' && src[i + 1] === '{') { depth += 1; out += '${'; i += 2; continue; }
    if (c === '}') { depth -= 1; if (depth === 0) return [out, i + 1]; out += '}'; i += 1; continue; }
    out += c; i += 1;
  }
  throw new ParseError('Unclosed interpolation');
}

/** Parse a single interpolation body: a path or a function call. */
function parseExpr(raw) {
  const expr = String(raw).trim();
  const paren = expr.indexOf('(');
  if (paren >= 0 && expr.endsWith(')')) {
    const name = expr.slice(0, paren).trim();
    if (!/^[@A-Za-z_][\w.-]*$/.test(name)) throw new ParseError(`Unexpected characters in function name "${name}"`);
    const args = parseArgs(expr.slice(paren + 1, -1));
    return { call: name, args, returnType: 'any' };
  }
  if (/^[@A-Za-z_/][\w./~-]*$/.test(expr) || expr.startsWith('/')) {
    return { path: normalizeExprPath(expr) };
  }
  throw new ParseError(`Unexpected characters in expression "${expr}"`);
}

function normalizeExprPath(expr) {
  // `${/a/b}` stays; `${rel}` stays relative; both usable by getPointer
  return expr;
}

/** Parse `name:value, name2:value2` into `{name→part}`. */
function parseArgs(raw) {
  const s = String(raw).trim();
  if (!s) return {};
  const args = {};
  const items = splitTopLevel(s, ',');
  for (const item of items) {
    const colon = topLevelIndexOf(item, ':');
    if (colon < 0) throw new ParseError('Expected ":" after argument name');
    const name = item.slice(0, colon).trim();
    if (!/^[A-Za-z_]\w*$/.test(name)) throw new ParseError(`Invalid argument name "${name}"`);
    args[name] = parseArgValue(item.slice(colon + 1).trim());
  }
  return args;
}

function parseArgValue(raw) {
  const v = raw.trim();
  if (v === 'true') return { literal: true };
  if (v === 'false') return { literal: false };
  if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
    return { literal: v.slice(1, -1) };
  }
  if (/^-?\d+$/.test(v)) return { literal: parseInt(v, 10) };
  if (/^-?\d*\.\d+([eE][+-]?\d+)?$/.test(v) || /^-?\d+[eE][+-]?\d+$/.test(v)) return { literal: Number(v) };
  if (/^-?\d/.test(v) && !/^[A-Za-z_/]/.test(v)) throw new ParseError(`Invalid number literal "${v}"`);
  const paren = v.indexOf('(');
  if (paren >= 0 && v.endsWith(')')) return parseExpr(v); // nested call
  return { path: normalizeExprPath(v) };
}

function splitTopLevel(s, sep) {
  const out = [];
  let depth = 0;
  let cur = '';
  let q = null;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (q) { cur += c; if (c === q) q = null; continue; }
    if (c === "'" || c === '"') { q = c; cur += c; continue; }
    if (c === '(') depth += 1;
    if (c === ')') depth -= 1;
    if (c === sep && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += c;
  }
  if (cur.trim() !== '' || out.length) out.push(cur);
  return out;
}

function topLevelIndexOf(s, ch) {
  let q = null;
  let depth = 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (q) { if (c === q) q = null; continue; }
    if (c === "'" || c === '"') { q = c; continue; }
    if (c === '(') depth += 1;
    if (c === ')') depth -= 1;
    if (c === ch && depth === 0) return i;
  }
  return -1;
}

// ─── expression / Dynamic evaluation ───────────────────────────────────────

const MAX_DEPTH = 64;

/**
 * Evaluate a Dynamic value against a data model. A Dynamic is:
 *   - a literal (string/number/bool/array/object with no `path`/`call`)
 *   - a DataBinding `{ path }`
 *   - a FunctionCall `{ call, args }`
 * `scope` provides relative-path base + `@index` for collection templates.
 */
export function evalDynamic(value, model, scope = {}, functions = CORE_FUNCTIONS, depth = 0) {
  if (depth > MAX_DEPTH) throw new Error('Max recursion depth exceeded');
  if (value == null) return value;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return value; // literal scalar

  // a parser literal node `{ literal: X }` (from parseTemplate) unwraps to X
  if ('literal' in value && !('path' in value) && !('call' in value)) return value.literal;

  if (typeof value.path === 'string' && !('call' in value)) {
    return readPath(value.path, model, scope);
  }
  if (typeof value.call === 'string') {
    // @index — the system collection-index function (needs the template scope)
    if (value.call === '@index') {
      const offset = value.args && value.args.offset != null
        ? Number(evalDynamic(value.args.offset, model, scope, functions, depth + 1)) : 0;
      return (scope && typeof scope.index === 'number' ? scope.index : 0) + offset;
    }
    const fn = functions[value.call];
    if (!fn) return undefined; // unknown function → absent (renderers degrade)
    const args = {};
    for (const [k, v] of Object.entries(value.args || {})) {
      args[k] = evalDynamic(v, model, scope, functions, depth + 1);
    }
    return fn(args, { model, scope, functions, depth });
  }
  return value; // object literal
}

function readPath(path, model, scope) {
  const get = typeof model?.get === 'function' ? (p) => model.get(p) : (p) => getPointer(model, p);
  if (path.startsWith('/')) return get(path);
  // relative → resolve against the current collection scope base
  if (scope && scope.base) {
    if (path === '') return get(scope.base); // the row itself
    return get(`${scope.base}/${path.replace(/^\//, '')}`);
  }
  return path === '' ? get('/') : get('/' + path);
}

export function stringifyValue(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}

// ─── core catalog functions (+ arithmetic for the conformance corpus) ──────

export const CORE_FUNCTIONS = {
  formatString: (args) => {
    // `value` was pre-resolved by evalDynamic; but formatString's own arg is a
    // TEMPLATE STRING that must be re-parsed against the model. We can't see the
    // model here without ctx, so this variant handles the already-resolved case;
    // the model-aware path lives in evalFormatString below.
    return stringifyValue(args.value);
  },
  formatNumber: (args) => formatNumber(args.value, args.decimals, args.grouping),
  formatCurrency: (args) => formatCurrency(args.value, args.currency, args.decimals, args.grouping),
  formatDate: (args) => formatDate(args.value, args.format),
  pluralize: (args) => pluralize(args),
  and: (args) => (args.values || []).every(Boolean),
  or: (args) => (args.values || []).some(Boolean),
  not: (args) => !args.value,
  required: (args) => validationResult(args.value != null && args.value !== '', 'required'),
  email: (args) => validationResult(/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(args.value ?? '')), 'email'),
  regex: (args) => validationResult(new RegExp(args.pattern).test(String(args.value ?? '')), 'regex'),
  length: (args) => validationResult(lengthOk(args), 'length'),
  numeric: (args) => validationResult(numericOk(args), 'numeric'),
  // arithmetic — not in the basic-14 but exercised by conformance/core/functions.yaml
  add: (args) => Number(args.a) + Number(args.b),
  subtract: (args) => Number(args.a) - Number(args.b),
  multiply: (args) => Number(args.a) * Number(args.b),
  divide: (args) => Number(args.a) / Number(args.b),
};

function validationResult(valid, name) {
  return valid ? { valid: true } : { valid: false, error: `${name} check failed` };
}
function lengthOk(args) {
  const n = String(args.value ?? '').length;
  if (args.min != null && n < args.min) return false;
  if (args.max != null && n > args.max) return false;
  return true;
}
function numericOk(args) {
  const n = Number(args.value);
  if (Number.isNaN(n)) return false;
  if (args.min != null && n < args.min) return false;
  if (args.max != null && n > args.max) return false;
  return true;
}

function formatNumber(value, decimals, grouping) {
  const n = Number(value);
  if (Number.isNaN(n)) return stringifyValue(value);
  const opts = { useGrouping: grouping !== false };
  if (decimals != null) { opts.minimumFractionDigits = decimals; opts.maximumFractionDigits = decimals; }
  return new Intl.NumberFormat('en-US', opts).format(n);
}
function formatCurrency(value, currency, decimals, grouping) {
  const n = Number(value);
  if (Number.isNaN(n)) return stringifyValue(value);
  const opts = { style: 'currency', currency: currency || 'USD', useGrouping: grouping !== false };
  if (decimals != null) { opts.minimumFractionDigits = decimals; opts.maximumFractionDigits = decimals; }
  return new Intl.NumberFormat('en-US', opts).format(n);
}
function formatDate(value, fmt) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return stringifyValue(value);
  const pad = (x, n = 2) => String(x).padStart(n, '0');
  const map = {
    yyyy: d.getUTCFullYear(), yy: pad(d.getUTCFullYear() % 100),
    MM: pad(d.getUTCMonth() + 1), M: d.getUTCMonth() + 1,
    dd: pad(d.getUTCDate()), d: d.getUTCDate(),
    HH: pad(d.getUTCHours()), mm: pad(d.getUTCMinutes()), ss: pad(d.getUTCSeconds()),
  };
  return String(fmt || 'yyyy-MM-dd').replace(/yyyy|yy|MM|M|dd|d|HH|mm|ss/g, (t) => map[t]);
}
function pluralize(args) {
  const n = Number(args.value);
  const cat = n === 0 && args.zero != null ? 'zero'
    : n === 1 && args.one != null ? 'one'
      : n === 2 && args.two != null ? 'two' : 'other';
  return String(args[cat] ?? args.other ?? '');
}

/**
 * Evaluate a `formatString` where the template arg string must be resolved
 * against the model (the model-aware path). Returns the interpolated string.
 */
export function evalFormatString(template, model, scope = {}, functions = CORE_FUNCTIONS) {
  const parts = parseTemplate(template);
  return parts.map((p) => {
    if ('literal' in p) return p.literal;
    if ('path' in p) return stringifyValue(readPath(p.path, model, scope));
    if ('call' in p) {
      if (p.call === 'formatString') {
        const inner = evalDynamic(p.args.value, model, scope, functions);
        return evalFormatString(stringifyValue(inner), model, scope, functions);
      }
      const args = {};
      for (const [k, v] of Object.entries(p.args || {})) args[k] = evalDynamic(v, model, scope, functions);
      const fn = functions[p.call];
      return fn ? stringifyValue(fn(args, { model, scope, functions })) : '';
    }
    return '';
  }).join('');
}

export { ParseError, DataError };
